import { unflattenArray } from "./SelectionUtils";

/**
 * Represents a line segment defined by its start and end points.
 */
export class LineSegment {
  constructor(start, end) {
    this.start = start;
    this.end = end;
  }
}

export const intersectionPointLocationEnum = {
  FIRST_LINE: "FIRST_LINE",
  SECOND_LINE: "SECOND_LINE",
  BOTH: "BOTH",
  NONE: "NONE"
};

/**
 * Calculates the intersection point of two line segments on extrapolation, if applicable.
 * @see {@link http://paulbourke.net/geometry/pointlineplane/}
 * @param {LineSegment} segment1 - The first line segment.
 * @param {LineSegment} segment2 - The second line segment.
 * @returns {Object|null} - The intersection point object with 'x' and 'y' coordinates, or null if there is no intersection on extrapolation.
 */
export function calculateIntersectionPoint(segment1, segment2) {
  const x1 = segment1.start.x;
  const y1 = segment1.start.y;
  const x2 = segment1.end.x;
  const y2 = segment1.end.y;

  const x3 = segment2.start.x;
  const y3 = segment2.start.y;
  const x4 = segment2.end.x;
  const y4 = segment2.end.y;

  const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);

  // Check if the lines are parallel or coincident
  if (denominator === 0) {
    return null;
  }

  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

  const checkRange = (value, rangeStart = 0, rangeEnd = 1) =>
    value >= rangeStart && value <= rangeEnd;

  /* 
    The extrapolated lines will definitely meet as the lines are neither parallel nor coincident
    Check if the intersection point lies on none/either/both of the line segments
  */
  const isPointOnLine1 = checkRange(ua);
  const isPointOnLine2 = checkRange(ub);

  const intersectionX = x1 + ua * (x2 - x1);
  const intersectionY = y1 + ua * (y2 - y1);
  let intersectionPointLocation;

  if (!isPointOnLine1 && !isPointOnLine2) {
    intersectionPointLocation = intersectionPointLocationEnum.NONE;
  } else if (isPointOnLine1 && isPointOnLine2)
    intersectionPointLocation = intersectionPointLocationEnum.BOTH;
  else if (isPointOnLine1)
    intersectionPointLocation = intersectionPointLocationEnum.FIRST_LINE;
  else if (isPointOnLine2)
    intersectionPointLocation = intersectionPointLocationEnum.SECOND_LINE;
  return { x: intersectionX, y: intersectionY, intersectionPointLocation };
}

/**
 * Calculates the perpendicular distance between a point and a line segment in 2D space.
 *
 * @param {Object} point - The coordinates of the point as an object with `x` and `y` properties.
 * @param {LineSegment} lineSegment - The line segment.
 * @returns {number} The perpendicular distance between the point and the line segment.
 *
 * @example
 * // Example usage:
 * const point = { x: 2, y: 3 };
 * const lineSegment = new LineSegment({ x: 0, y: 0 }, { x: 4, y: 0 });
 * const distance = pointToLineSegmentDistance(point, lineSegment);
 * console.log(distance);
 *
 * @see {@link http://paulbourke.net/geometry/pointlineplane/}
 *
 * @description
 * Calculates the perpendicular distance between a point and a line segment in 2D space.
 * The closest point on the line segment to the given point is determined, and the distance
 * between the point and the closest point is calculated. If the perpendicular distance of
 * the point does not lie on the line segment, the closest point considered will be the
 * corresponding endpoint of the line segment.
 */
export function pointToLineSegmentDistance(point, lineSegment) {
  const { start, end } = lineSegment;
  // Calculate the components of the vector from the start point to the point in question
  const vectorX = point.x - start.x;
  const vectorY = point.y - start.y;

  // Calculate the components of the vector representing the line segment
  const segmentX = end.x - start.x;
  const segmentY = end.y - start.y;

  // Calculate the dot product of the two vectors
  const dotProduct = vectorX * segmentX + vectorY * segmentY;

  // Calculate the squared length of the line segment
  const segmentLengthSq = segmentX * segmentX + segmentY * segmentY;

  // Calculate the parameter for the closest point on the line segment
  let closestPointParam = -1;

  if (segmentLengthSq !== 0) {
    closestPointParam = dotProduct / segmentLengthSq;
  }

  let closestX;
  let closestY;

  // If the closest point falls outside the line segment, set it to the corresponding endpoint
  if (closestPointParam < 0) {
    // The closest point is before the start point of the line segment
    closestX = start.x;
    closestY = start.y;
  } else if (closestPointParam > 1) {
    // The closest point is after the end point of the line segment
    closestX = end.x;
    closestY = end.y;
  } else {
    // Calculate the coordinates of the closest point on the line segment
    closestX = start.x + closestPointParam * segmentX;
    closestY = start.y + closestPointParam * segmentY;
  }

  // Calculate the distance between the point and the closest point on the line segment
  const dx = point.x - closestX;
  const dy = point.y - closestY;
  const distance = Math.sqrt(dx * dx + dy * dy);

  return distance;
}

/**
 * This function returns the distance bw two given points.
 * @param {Object} pt1 object having two keys: x & y coordinates
 * @param {Object} pt2 object having two keys: x & y coordinates
 * @returns {Number} distance bw the points
 */
export const findDistanceBwPoints = (pt1, pt2) => {
  const { x: x1, y: y1 } = pt1;
  const { x: x2, y: y2 } = pt2;
  const euclidianDistance = Math.sqrt(
    (x1 - x2) * (x1 - x2) + (y2 - y1) * (y2 - y1)
  );
  return euclidianDistance;
};

/**
 * Convert points array to array of LineSegments in following format
 * [x1,y1, x2,y2, x3,y3, x4,y4]  => [{ start: {x1,y1}, end:{x2,y2} }, { start: {x3,y3}, end:{x4,y4} }]
 *
 * @param {Array} pointsArray
 * @returns {Array<LineSegment>} LineSegment Array
 */
export const getLineSegmentsFromPointsArray = pointsArray =>
  unflattenArray(pointsArray, 4).map(lineSegmentPoints => {
    const [x1, y1, x2, y2] = lineSegmentPoints;
    return new LineSegment({ x: x1, y: y1 }, { x: x2, y: y2 });
  });

/**
 * Returns the mid point of the line segment
 * @param {LineSegment} lineSegment
 * @returns {Object} {x,y} midpoint of the line segment
 */
export const getMidPointLineSegment = lineSegment => {
  const { start, end } = lineSegment;
  const midPoint = {
    x: (start.x + end.x) / 2,
    y: (start.y + end.y) / 2
  };
  return midPoint;
};

/**
 * check if two quadrilaterals overlap using the Separating Axis Theorem (SAT):
 * @param q1
 * @param q2
 * @return {boolean}
 */
export const doQuadrilateralsOverlap = (q1, q2) => {
  // Extract coordinates for quadrilateral 1
  const q1Vertices = [
    [q1[0], q1[1]],
    [q1[2], q1[3]],
    [q1[4], q1[5]],
    [q1[6], q1[7]]
  ];

  // Extract coordinates for quadrilateral 2
  const q2Vertices = [
    [q2[0], q2[1]],
    [q2[2], q2[3]],
    [q2[4], q2[5]],
    [q2[6], q2[7]]
  ];

  // Function to get axes for SAT
  function getAxes(vertices) {
    const axes = [];
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < vertices.length; i++) {
      const p1 = vertices[i];
      const p2 = vertices[(i + 1) % vertices.length];
      // Calculate the perpendicular vector as the axis
      const axis = { x: -(p2[1] - p1[1]), y: p2[0] - p1[0] };
      // Normalize the axis
      const length = Math.sqrt(axis.x * axis.x + axis.y * axis.y);
      axes.push({ x: axis.x / length, y: axis.y / length });
    }
    return axes;
  }

  // Function to project vertices onto an axis and return min/max values
  function project(vertices, axis) {
    let min = Infinity;
    let max = -Infinity;
    // eslint-disable-next-line no-restricted-syntax
    for (const vertex of vertices) {
      const projection = vertex[0] * axis.x + vertex[1] * axis.y;
      if (projection < min) min = projection;
      if (projection > max) max = projection;
    }
    return { min, max };
  }

  // Get axes for both quadrilaterals
  const axes = [...getAxes(q1Vertices), ...getAxes(q2Vertices)];

  // Function to check if projections overlap on a given axis
  function overlapOnAxis(axis) {
    const proj1 = project(q1Vertices, axis);
    const proj2 = project(q2Vertices, axis);
    return !(proj1.max < proj2.min || proj2.max < proj1.min);
  }

  // Check overlap for all axes
  // eslint-disable-next-line no-restricted-syntax
  for (const axis of axes) {
    if (!overlapOnAxis(axis)) {
      return false; // Separating axis found, no overlap
    }
  }
  return true; // No separating axis found, overlap exists
};

/**
 * This method checks if the pair of quads can be arranged top to bottom only and not adjacent so that
 * they can properly merged vertically.
 * It calculates this by checking if the bottom line of the top quad intersects with left/right of the bottom quad
 * Similar calculation is done for the top line of the bottom quad with left/right of the top quad.
 * @param {VerticallyMergedQuad} currentVerticalQuad This quad should be on top wrt reading orientation
 * @param {VerticallyMergedQuad} nextVerticalQuad This quad should be at bottom order wrt reading orientation
 * @returns False if the quads are adjacent and true if they are not adjacent
 */
export const checkIfQuadsAreNotAdjacent = (
  currentVerticalQuad,
  nextVerticalQuad
) => {
  const currentQuad = currentVerticalQuad.bottomMostQuad;
  const nextQuad = nextVerticalQuad.topMostQuad;

  /*
   First check the bottom edge of the first quad with the right/left edges of the next quad.
   If no intersection point is found, then check the top edge of the next quad with the right/left
   edges of the current quad.
   */

  // check if the bottom edge of the current quad intersects with the right edge of the next quad
  // line segment is created from right to left
  const bottomEdgeCurrentQuad = new LineSegment(
    currentQuad.bottomRight,
    currentQuad.bottomLeft
  );
  const rightEdgeNextQuad = new LineSegment(
    nextQuad.topRight,
    nextQuad.bottomRight
  );
  let intersectionPoint = calculateIntersectionPoint(
    bottomEdgeCurrentQuad,
    rightEdgeNextQuad
  );

  let areQuadsIntersecting =
    intersectionPoint &&
    (intersectionPoint.intersectionPointLocation ===
      intersectionPointLocationEnum.FIRST_LINE ||
      intersectionPoint.intersectionPointLocation ===
        intersectionPointLocationEnum.BOTH);

  if (areQuadsIntersecting) return true;

  // check if the bottom edge of the current quad intersects with the left edge of the next quad
  const leftEdgeNextQuad = new LineSegment(
    nextQuad.topLeft,
    nextQuad.bottomLeft
  );
  intersectionPoint = calculateIntersectionPoint(
    bottomEdgeCurrentQuad,
    leftEdgeNextQuad
  );

  areQuadsIntersecting =
    intersectionPoint &&
    (intersectionPoint.intersectionPointLocation ===
      intersectionPointLocationEnum.FIRST_LINE ||
      intersectionPoint.intersectionPointLocation ===
        intersectionPointLocationEnum.BOTH);

  if (areQuadsIntersecting) return true;

  // Now check the top edge of the next quad with the right/left edges of the current quad

  // check if the right edge of the current quad intersects with the top edge of the next quad
  const rightEdgeCurrentQuad = new LineSegment(
    currentQuad.topRight,
    currentQuad.bottomRight
  );
  const topEdgeNextQuad = new LineSegment(nextQuad.topRight, nextQuad.topLeft);

  let intersectionPointTwo = calculateIntersectionPoint(
    rightEdgeCurrentQuad,
    topEdgeNextQuad
  );
  areQuadsIntersecting =
    intersectionPointTwo &&
    (intersectionPointTwo.intersectionPointLocation ===
      intersectionPointLocationEnum.SECOND_LINE ||
      intersectionPointTwo.intersectionPointLocation ===
        intersectionPointLocationEnum.BOTH);
  if (areQuadsIntersecting) return true;

  // check if the left edge of the current quad intersects with the top edge of the next quad
  const leftEdgeCurrentQuad = new LineSegment(
    currentQuad.topLeft,
    currentQuad.topRight
  );

  intersectionPointTwo = calculateIntersectionPoint(
    leftEdgeCurrentQuad,
    topEdgeNextQuad
  );
  areQuadsIntersecting =
    intersectionPointTwo &&
    (intersectionPointTwo.intersectionPointLocation ===
      intersectionPointLocationEnum.SECOND_LINE ||
      intersectionPointTwo.intersectionPointLocation ===
        intersectionPointLocationEnum.BOTH);

  return areQuadsIntersecting;
};

/**
 * Checks if two edges intersect in a two-dimensional space.
 * Each edge is represented as an array in the format [x1,y1, x2,y2, x3,y3, x4,y4]
 * where x(i),y(i) are the coordinates of the ith point on the edge.
 *
 * @param {Array} edgePointsArray1 - The array of points defining the first edge.
 * @param {Array} edgePointsArray2 - The array of points defining the second edge.
 * @returns {boolean} True if the edges intersect, false otherwise.
 *
 * @example
 * // Example usage:
 * // Define the points for the first edge [x1,y1, x2,y2]
 * const edgePointsArray1 = [0, 0, 2, 2];
 * // Define the points for the second edge [x1,y1, x2,y2]
 * const edgePointsArray2 = [1, 0, 1, 3];
 *
 * // Check if the edges intersect
 * const edgesIntersect = checkIfEdgesIntersect(edgePointsArray1, edgePointsArray2);
 * console.log(edgesIntersect); // Output: true
 */
export const checkIfEdgesIntersect = (edgePointsArray1, edgePointsArray2) => {
  const edgeLineSegments1 = getLineSegmentsFromPointsArray(edgePointsArray1);
  const edgeLineSegments2 = getLineSegmentsFromPointsArray(edgePointsArray2);
  return edgeLineSegments1.some(lineSegment1 =>
    edgeLineSegments2.some(lineSegment2 => {
      const intersectionPoint = calculateIntersectionPoint(
        lineSegment1,
        lineSegment2
      );
      return (
        intersectionPoint &&
        intersectionPoint.intersectionPointLocation ===
          intersectionPointLocationEnum.BOTH
      );
    })
  );
};

/**
 * Find if the lowest distance bw edges in a 2-D space is less than given threshold.
 * Each edge is represented as an array in the format [x1,y1, x2,y2, x3,y3, x4,y4]
 * where x(i),y(i) are the coordinates of the ith point on the edge.
 *
 * @param {Array} edgePointsArray1 - The array of points defining the first edge.
 * @param {Array} edgePointsArray2 - The array of points defining the second edge.
 * @param {number} thresholdDistance - The threshold distance to be used for merging the edges.
 * @returns {boolean} True if the edges intersect, false otherwise.
 *
 * @example
 * // Example usage:
 * // Define the points for the first edge [x1,y1, x2,y2]
 * const edgePointsArray1 = [0, 0, 2, 2];
 * // Define the points for the second edge [x1,y1, x2,y2]
 * const edgePointsArray2 = [1, 0, 1, 3];
 *
 * // Check if the edges intersect
 * const getMinDistanceInfoBwEdges = getMinDistanceInfoBwEdges(edgePointsArray1, edgePointsArray2, 1.5);
 * console.log(getMinDistanceInfoBwEdges); // Output: { shouldMerge: true, minDistance: 0.7}
 */
export const getMinDistanceInfoBwEdges = (
  edgePointsArray1,
  edgePointsArray2,
  thresholdDistance
) => {
  const minEdgeDistCalculator = (edgePointsArray, lineSegmentArray) => {
    // This methods takes an array of edge points and returns an array of edge points with mid points
    // injected to it. This is done to get more accurate distance bw the edges.
    const getEdgePointsArrayWithMidPoints = edgePoints => {
      const edgeLineSegments = getLineSegmentsFromPointsArray(edgePoints);
      return edgeLineSegments.reduce((finalArray, lineSegment) => {
        const { start, end } = lineSegment;
        const mid = getMidPointLineSegment(lineSegment);
        finalArray.push(...[start, mid, end]);
        return finalArray;
      }, []);
    };

    let minDistanceInfo = {
      minDistance: Number.MAX_SAFE_INTEGER,
      shouldMerge: false
    };

    // add mid points to the edge points array but keeping the line segments the same
    // to get more accurate distance bw the edges
    const edgePointsArrayWithMidPoints = getEdgePointsArrayWithMidPoints(
      edgePointsArray
    );
    edgePointsArrayWithMidPoints.forEach(point => {
      lineSegmentArray.forEach(lineSegment => {
        const distance = pointToLineSegmentDistance(point, lineSegment);
        const shouldMerge = distance <= thresholdDistance;
        if (shouldMerge && distance < minDistanceInfo.minDistance) {
          minDistanceInfo = { shouldMerge, minDistance: distance };
        }
      });
    });
    return minDistanceInfo;
  };

  // finding the distance of the points on edge1 from edge2 line segments
  const edge2LineSegments = getLineSegmentsFromPointsArray(edgePointsArray2);
  const minDistanceInfo1 = minEdgeDistCalculator(
    edgePointsArray1,
    edge2LineSegments
  );
  // finding the distance of the points on edge2 from edge1 line segments
  const edge1LineSegments = getLineSegmentsFromPointsArray(edgePointsArray1);
  const minDistanceInfo2 = minEdgeDistCalculator(
    edgePointsArray2,
    edge1LineSegments
  );

  const minDistanceInfo = {
    shouldMerge: false,
    minDistance: Number.MAX_SAFE_INTEGER
  };

  if (minDistanceInfo1.shouldMerge && minDistanceInfo.shouldMerge) {
    return {
      shouldMerge: true,
      minDistance: Math.min(
        minDistanceInfo1.minDistance,
        minDistanceInfo2.minDistance
      )
    };
  } else if (minDistanceInfo1.shouldMerge) {
    return minDistanceInfo1;
  } else if (minDistanceInfo2.shouldMerge) {
    return minDistanceInfo2;
  }
  return minDistanceInfo;
};
